import {CommonModule} from '@angular/common';
import {Component, OnDestroy, OnInit} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatCardModule} from '@angular/material/card';
import {MatDividerModule} from '@angular/material/divider';
import {MatIconModule} from '@angular/material/icon';
import {Entite, InterneExterne} from '../../../../models/entite/EntiteResponse';
import {UtilisateurService} from '../../../../services/utilisateurs/utilisateur.service';
import {ActivatedRoute, Router} from '@angular/router';
import {EntiteService} from '../../../../services/entites/entite.service';
import {forkJoin, map, Observable, of, startWith, Subscription, switchMap} from 'rxjs';
import {MatSelectModule} from '@angular/material/select';
import {MatInputModule} from '@angular/material/input';
import {MatButtonModule} from '@angular/material/button';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {MatAutocompleteModule} from '@angular/material/autocomplete';
import {UtilisateurBody} from '../../../../models/utilisateur/UtilisateurBody';
import {
  ProfilsEntitesUtilisateurBody
} from '../../../../models/profilsEntitesUtilisateur/ProfilsEntitesUtilisateurBody';
import {
  ProfilsEntitesUtilisateursService
} from '../../../../services/profils-entites-utilisateurs/profils-entites-utilisateurs.service';
import {Adb2cBody, Identity, PasswordProfile} from '../../../../models/adb2c/Adb2cBody';
import {Adb2cService} from '../../../../services/adb2c/adb2c.service';
import {Adb2cBodyPatch} from '../../../../models/adb2c/Adb2cBodyPatch';
import {environment} from "../../../../../environments/environment.local";
import {MatDialog, MatDialogModule} from '@angular/material/dialog';
import {MatSnackBar, MatSnackBarModule, MatSnackBarVerticalPosition} from '@angular/material/snack-bar';
import {
  AjouterModifierProfilDialogueComponent
} from './ajouter-modifier-profil-dialogue/ajouter-modifier-profil-dialogue.component';

import {
  ProfilsEntitesUtilisateur
} from '../../../../models/profilsEntitesUtilisateur/ProfilsEntitesUtilisateurResponse';
import {BreadcrumbComponent} from '../../../breadcrumb/breadcrumb.component';

@Component({
  selector: 'app-creer-modifier-utilisateur',
  standalone: true,
  imports: [
    MatIconModule, MatCardModule, CommonModule, MatDividerModule, MatSelectModule,
    ReactiveFormsModule, MatInputModule, MatButtonModule, MatCheckboxModule, MatAutocompleteModule,
    MatDialogModule, MatSnackBarModule, BreadcrumbComponent
  ],
  templateUrl: './creer-modifier-utilisateur.component.html',
  styleUrl: './creer-modifier-utilisateur.component.scss'
})
export class CreerModifierUtilisateurComponent implements OnInit, OnDestroy {
  userForm: FormGroup;
  entities: Entite[] = [];
  filteredEntities: Observable<Entite[]> = of([]);
  isEditMode: boolean = false;
  userId: string = '';
  currentUser: UtilisateurBody | null = null;
  erroredUserId: number | undefined;
  selections: ProfilsEntitesUtilisateurBody[] = [];
  initialSelections: ProfilsEntitesUtilisateur[] = [];
  isSelectionsEmpty = false;
  isSelectionsModified = false;
  profilsMap: { [id: number]: string } = {
    5: 'Administrateur OPUS',
    6: 'Agent terrain',
    7: 'PCSI',
    // 8: 'PCL',
    9: 'Direction Exploitation',
    10: 'Chargé de mission',
    11: 'Direction Service Client',
  };
  formSubmitted: boolean = false;
  verticalPosition: MatSnackBarVerticalPosition = 'top';

  subscriptions: Subscription[] = [];

  constructor(
    private entiteService: EntiteService,
    private utilisateurService: UtilisateurService,
    private adb2cService: Adb2cService,
    private profilsEntitesUtilisateursService: ProfilsEntitesUtilisateursService,
    private route: ActivatedRoute,
    private router: Router,
    public dialog: MatDialog,
    public snackBar: MatSnackBar,
  ) {
    this.userForm = new FormGroup({
      entite: new FormControl('', Validators.required),
      kiwinet: new FormControl(false),
      nom: new FormControl('', Validators.required),
      prenom: new FormControl('', Validators.required),
      email: new FormControl('', [Validators.required, Validators.email]),
      telephone: new FormControl('', [Validators.required, Validators.pattern('^0[0-9]{9}$')]),
      accountEnabled: new FormControl(true)
    });
  }

  ngOnInit() {
    this.loadEntities();
    this.setupEditMode();
  }

  private loadEntities() {
    const params = { limit: 100 };
    const entitySubscription = this.entiteService.getEntities(params).subscribe({
      next: (response) => {
        this.entities = response.data;
        this.filteredEntities = this.userForm.controls['entite'].valueChanges.pipe(
          startWith(''),
          map(value => (typeof value === 'string' ? value : value.libelle)),
          map(libelle => libelle ? this.filterEntities(libelle) : this.entities.slice())
        );
      },
      error: (error) => console.error('Error loading entities', error)
    });
    this.subscriptions.push(entitySubscription);
  }

  private setupEditMode() {
    this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      if (id) {
        this.userId = id;
        this.isEditMode = true;
        this.loadUserDetails(this.userId);
        this.loadUserProfilsEntites(this.userId);
      }
    });
  }

  private loadUserDetails(userId: string) {
    const userSubscription = this.utilisateurService.getUserById(userId).subscribe({
      next: (response) => {
        const user = response.data;
        this.currentUser = user;
        this.userForm.patchValue({
          entite: user.entite,
          kiwinet: user.keolisAADAccount,
          nom: user.givenName,
          prenom: user.surname,
          email: user.mail,
          telephone: user.mobilePhone
        });
      },
      error: (error) => console.error('Error loading user', error)
    });
    this.subscriptions.push(userSubscription);
    this.userForm.get('email')?.disable();
  }

  private loadUserProfilsEntites(userId: string) {
    const profilEntitySubscription = this.profilsEntitesUtilisateursService.getProfilEntityUser({ users_id: userId }).subscribe({
      next: (response) => {
        this.initialSelections = response.data;
        this.selections = response.data.map(item => ({
          users_id: item.users_id,
          entite_id: item.entite_id,
          profil_id: item.profil_id
        }));
      },
      error: (error) => console.error('Error loading profil entities', error)
    });
    this.subscriptions.push(profilEntitySubscription);
  }

  async onSubmit() {
    this.formSubmitted = true;
    const formValue = this.userForm.value;

    if (this.userForm.invalid || this.selections.length === 0) {
      alert('Le formulaire contient des erreurs, veuillez les corriger avant de soumettre !');
      this.isSelectionsEmpty = this.selections.length === 0 ? true : false;
      return;
    }
    const utilisateur: UtilisateurBody = new UtilisateurBody(
      formValue.nom,
      formValue.prenom,
      `${formValue.nom} ${formValue.prenom}`,
      formValue.email,
      formValue.telephone,
      true,
      formValue.kiwinet,
      formValue.entite.id
    );

    const identity: Identity = new Identity(
      'emailAddress',
      environment.issuerNameOnMicrosoft,
      formValue.email
    )
    const passwordProfile: PasswordProfile = new PasswordProfile(
      this.generatePassword()
    )

    const utilisateurAdb2c: Adb2cBody = new Adb2cBody(
      true,
      formValue.nom,
      formValue.prenom,
      `${formValue.nom} ${formValue.prenom}`,
      `${formValue.prenom.charAt(0).toUpperCase()}${formValue.nom}`,
      'LocalAccount',
      formValue.telephone,
      [identity],
      passwordProfile,
      formValue.email
    );
    console.log(`${formValue.nom}${formValue.prenom.charAt(0).toUpperCase()}`);

    try {
      if (!this.isEditMode) {
        if (formValue.kiwinet) {
          await this.handleKiwinetChecked(utilisateur);
        } else {
          await this.handleKiwinetUnchecked(utilisateur, utilisateurAdb2c);
        }
      } else {
        this.onUpdateUser();
      }
      setTimeout(() => {
        window.location.reload();
      }, 200);
    } catch (error) {
      console.error('Erreur lors de la création de l\'utilisateur :', error);
    }
  }

  private handleKiwinetChecked(utilisateur: UtilisateurBody) {
    const userPostSubscription = this.utilisateurService.postUser(utilisateur).pipe(
      switchMap(response => {
        const profilsEntites = this.selections.map(selection => ({
          ...selection,
          users_id: response.data.id,
        }));

        return this.profilsEntitesUtilisateursService.postProfilEntityUser(profilsEntites)
      })
    ).subscribe({
      next: () => {
        console.log('User and profiles created successfully');
        this.snackBar.open('Utilisateur créé avec succès !', 'Fermer', {
          verticalPosition: this.verticalPosition,
          duration: 5000,
          panelClass: ['snackbar-success']
        });
        this.router.navigate(['administration/utilisateurs']);
      },
      error: (error) => {
          this.utilisateurService.getUsers({mail: utilisateur.mail}).subscribe({
            next: (response) => {
              if (response.data.length > 0 && response.data[0].profils.some(p => p.id_application === 2)) {
                this.erroredUserId = response.data[0].id
                this.snackBar.open('Erreur lors de la création de l’utilisateur: Profil(s) OPUS liée à cette adresse mail existe déjà.', 'Fermer', {
                  verticalPosition: this.verticalPosition,
                  duration: 5000,
                  panelClass: ['snackbar-error']
                });
                this.userForm.controls['email'].setErrors({ emailExists: true });
              } else {
                const updateProfils = this.selections.map(selection => ({
                  ...selection,
                  users_id: response.data[0].id,
                }));
                this.profilsEntitesUtilisateursService.postProfilEntityUser(updateProfils).subscribe({
                  next : () => {
                    console.log('Profile(s) added to OPUS successfully');
                    this.snackBar.open('Profil(s) ajouté à OPUS avec succès !', 'Fermer', {
                      verticalPosition: this.verticalPosition,
                      duration: 5000,
                      panelClass: ['snackbar-success']
                    });
                    this.router.navigate(['administration/utilisateurs']);
                  }
                });
              }
            },
            error: (userError) => console.error('Error fetching user by email', userError)
          });
      }
    });
    this.subscriptions.push(userPostSubscription);
  }

  private handleKiwinetUnchecked(utilisateur: UtilisateurBody, utilisateurAdb2c: Adb2cBody) {
    const adb2cPostSubscription = this.adb2cService.postAdb2c(utilisateurAdb2c).pipe(
      switchMap(responseAdb2c => {
        const id_adb2c = responseAdb2c.data.id;
        const userPrincipalName = responseAdb2c.data.userPrincipalName;

        return this.utilisateurService.postUser({...utilisateur, id_adb2c, userPrincipalName}).pipe(
          switchMap(responseUser => {
            const profilsEntites = this.selections.map(selection => ({
              ...selection,
              users_id: responseUser.data.id,
            }));
            return this.profilsEntitesUtilisateursService.postProfilEntityUser(profilsEntites)
          })
        );
      })
    ).subscribe({
      next: () => {
        console.log('User and profiles created successfully');
        this.snackBar.open('Utilisateur créé avec succès !', 'Fermer', {
          verticalPosition: this.verticalPosition,
          duration: 5000,
          panelClass: ['snackbar-success']
        });
        this.router.navigate(['administration/utilisateurs']);
      },
      error: (error) => {
        this.utilisateurService.getUsers({mail: utilisateur.mail}).subscribe({
          next: (response) => {
            if (response.data.length > 0 && response.data[0].profils.some(p => p.id_application === 2)) {
              this.erroredUserId = response.data[0].id
              this.snackBar.open('Erreur lors de la création de l’utilisateur: Profil(s) OPUS liée à cette adresse mail existe déjà.', 'Fermer', {
                verticalPosition: this.verticalPosition,
                duration: 5000,
                panelClass: ['snackbar-error']
              });
              this.userForm.controls['email'].setErrors({ emailExists: true });
            } else {
              const updateProfils = this.selections.map(selection => ({
                ...selection,
                users_id: response.data[0].id,
              }));
              this.profilsEntitesUtilisateursService.postProfilEntityUser(updateProfils).subscribe({
                next : () => {
                  console.log('Profile(s) added to OPUS successfully');
                  this.snackBar.open('Profil(s) ajouté à OPUS avec succès !', 'Fermer', {
                    verticalPosition: this.verticalPosition,
                    duration: 5000,
                    panelClass: ['snackbar-success']
                  });
                  this.router.navigate(['administration/utilisateurs']);
                }
              });
            }
          },
          error: (userError) => console.error('Error fetching user by email', userError)
        });
      }
    });
    this.subscriptions.push(adb2cPostSubscription);
  }

  private onUpdateUser(){
    const formValue = this.userForm.value;
    const utilisateurToUpdate: UtilisateurBody = {
      givenName: formValue.nom,
      surname: formValue.prenom,
      displayName: `${formValue.nom} ${formValue.prenom}`,
      mail: formValue.mail,
      mobilePhone: formValue.telephone,
      accountEnabled: this.currentUser?.accountEnabled ?? true,
      keolisAADAccount: formValue.kiwinet,
      entite_id: formValue.entite.id,
      userPrincipalName: this.currentUser?.userPrincipalName || undefined,
      id_adb2c: this.currentUser?.id_adb2c || undefined
    };
    const adb2cToUpdate: Adb2cBodyPatch = {
      givenName: formValue.nom,
      surname: formValue.prenom,
      displayName: `${formValue.nom} ${formValue.prenom}`,
      mailNickname: `${formValue.prenom.charAt(0).toUpperCase()}${formValue.nom}`,
      mobilePhone: formValue.telephone,
      mail: formValue.mail
    };
    let updates = [];
    const { added, removed } = this.compareSelections();

    if (added.length > 0) {
      const updateprofilsEntites$ = this.profilsEntitesUtilisateursService.postProfilEntityUser(added);
      updates.push(updateprofilsEntites$);
    }

    removed.forEach(id => {
      updates.push(this.profilsEntitesUtilisateursService.deleteProfilEntityUser(id));
    });

    if (this.userId) {
      const updateUser$ = this.utilisateurService.updateUser(this.userId, utilisateurToUpdate);
      updates.push(updateUser$);
    }
    if (this.currentUser?.id_adb2c) {
      const updateAdb2c$ = this.adb2cService.updateAdb2c(this.currentUser.id_adb2c, adb2cToUpdate);
      updates.push(updateAdb2c$);
    }

    if (updates.length > 0) {
      const userUpdateSubscription = forkJoin(updates).subscribe({
        next: (responses) => {
          console.log('User updated successfuly', responses);
          this.snackBar.open('Utilisateur mis à jour avec succès !', 'Fermer', {
            verticalPosition: this.verticalPosition,
            duration: 5000,
            panelClass: ['snackbar-success']
          });
          this.router.navigate(['administration/utilisateurs']);
        },
        error: (error) => {
          console.error('Error updating the user', error);
          this.snackBar.open('Erreur lors de la mise à jour de l’utilisateur !', 'Fermer', {
            verticalPosition: this.verticalPosition,
            duration: 5000,
            panelClass: ['snackbar-error']
          });
        }
      });
      this.subscriptions.push(userUpdateSubscription);
    } else {
      console.log('No user to update !');
    }
  }

  private compareSelections() {
    const added = this.selections.filter(s => !this.initialSelections.some(init => init.profil_id === s.profil_id && init.entite_id === s.entite_id));
    const removed = this.initialSelections.filter(init => !this.selections.some(s => s.profil_id === init.profil_id && s.entite_id === init.entite_id)).map(item => item.id);

    return { added, removed };
  }

  private generatePassword(length: number = 8): string {
    const charset = {
      lowercase: 'abcdefghijklmnopqrstuvwxyz',
      uppercase: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
      numeric: '0123456789',
      special: '!@#$%^&*()_+{}:"<>?[];',
    };
    const randomChar = (type: keyof typeof charset) => charset[type].charAt(Math.floor(Math.random() * charset[type].length));
    let password = [
      randomChar('lowercase'),
      randomChar('uppercase'),
      randomChar('numeric'),
      randomChar('special')
    ];
    const allChars = Object.values(charset).join('');
    for (let i = password.length; i < length; i++) {
      password.push(allChars.charAt(Math.floor(Math.random() * allChars.length)));
    }
    password = password.sort(() => Math.random() - 0.5);

    return password.join('');
  }

  private filterEntities(libelle: string): Entite[] {
    return this.entities.filter(entity =>
      entity.libelle.toLowerCase().includes(libelle.toLowerCase())
    );
  }

  displayFn = (entite?: Entite): string => {
    return entite ? `${entite.libelle} (${this.getInterneExterneLabel(entite.interne)})` : '';
  }

  isEntiteInterne(): boolean {
    const entiteId = this.userForm.get('entite')?.value.id;
    const entite = this.entities?.find(e => e.id === entiteId);
    return !!entite?.interne;
  }

  getInterneExterneLabel = (isInterne: boolean): string => {
    return isInterne ? InterneExterne.Interne : InterneExterne.Externe;
  };

  ouvrirDialogueAjouterProfil(): void {
    const entitesIdsSelected = this.selections.map(s => s.entite_id);
    const entitesavailable = this.entities.filter(entite => !entitesIdsSelected.includes(entite.id));
    let entiteSelectionnee = this.userForm.get('entite')?.value;

    if (!entitesavailable.some(e => e.id === entiteSelectionnee.id)) {
      entiteSelectionnee = null;
  }
    const dialogRef = this.dialog.open(AjouterModifierProfilDialogueComponent, {
      width: '620px',
      height: '275px',
      data: { entiteSelectionnee: entiteSelectionnee,
              entites: entitesavailable,
              selections: this.selections,
              editMode: false }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        result.forEach((selection: ProfilsEntitesUtilisateurBody) => {
          const exists = this.selections.some(existingSelection =>
            existingSelection.profil_id === selection.profil_id &&
            existingSelection.entite_id === selection.entite_id
          );
          if (!exists) {
            this.selections.push({
              users_id: Number(this.userId),
              profil_id: selection.profil_id,
              entite_id: selection.entite_id
            });
          }
        });
        console.log('Données reçues du dialogue Profil : ', this.selections);
        this.isSelectionsEmpty = this.selections.length === 0;
        this.isSelectionsModified = true;
      }
    });
  }

  ouvrirDialogueModifierProfil(entiteId: number): void {
    const initialProfils = this.selections.filter(s => s.entite_id === entiteId);
    const initialProfilIds = initialProfils.map(s => s.profil_id);

    if (initialProfils.length > 0) {
      const dialogRef = this.dialog.open(AjouterModifierProfilDialogueComponent, {
        width: '620px',
        height: '275px',
        data: {
          entiteSelectionnee: this.entities.find(e => e.id === entiteId),
          profilIds: initialProfilIds,
          entites: this.entities,
          editMode: true
        }
      });

      dialogRef.afterClosed().subscribe(result => {
        if(result) {
          const updatedProfilIds = result.map((selection: ProfilsEntitesUtilisateurBody) => selection.profil_id);

        result.forEach((selection: ProfilsEntitesUtilisateurBody) => {
          const index = this.selections.findIndex(s => s.profil_id === selection.profil_id && s.entite_id === entiteId);
          if (index !== -1) {
            this.selections[index] = { ...selection, users_id: Number(this.userId) };
          } else {
            this.selections.push({ ...selection, users_id: Number(this.userId) });
          }
        });

        this.selections = this.selections.filter(s => {
          return s.entite_id !== entiteId || updatedProfilIds.includes(s.profil_id);
        });

        this.isSelectionsEmpty = this.selections.length === 0;
        this.isSelectionsModified = true;
        console.log('Final selections after modifications:', this.selections);
        }

      });
    } else {
      console.log("Aucun profil trouvé pour cette entité");
    }
  }

  groupSelectionsByEntity() {
    const grouped = new Map<number, { profils: string[], entiteId: number }>();

    this.selections.forEach(selection => {
      if ([5, 6, 7, 8, 9, 10, 11].includes(selection.profil_id)) {
        const existing = grouped.get(selection.entite_id) || { profils: [], entiteId: selection.entite_id };
        existing.profils.push(this.profilsMap[selection.profil_id]);
        grouped.set(selection.entite_id, existing);
      }
    });

    return Array.from(grouped.values()).map(group => ({
      entiteLibelle: this.getEntiteLibelle(group.entiteId),
      profils: group.profils.join(', '),
      entiteId: group.entiteId
    }));
  }

  getEntiteLibelle(entiteId: number): string {
    const entite = this.entities.find(e => e.id === entiteId);
    return entite ? entite.libelle : 'Entité Inconnue';
  }

  removeEntityProfils(entiteId: number) {
    this.selections = this.selections.filter(selection => selection.entite_id !== entiteId);
    this.isSelectionsModified = true;
  }

  redirectToUserList() {
    this.router.navigate(['administration/utilisateurs']);
  }

  redirectToEditUser() {
    this.router.navigate(['administration/utilisateurs/modifier-utilisateur', this.erroredUserId]);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(x => x.unsubscribe());
  }
}
